// src/errors/XoneMacroError.ts
var XoneMacroError = class _XoneMacroError extends Error {
  code;
  constructor(code, message) {
    super(message);
    this.name = "XoneMacroError";
    this.code = code;
    Object.setPrototypeOf(this, _XoneMacroError.prototype);
  }
};

// src/listeners/MacroChangeBus.ts
var MacroChangeBus = class {
  /** Lista ordenada de listeners; orden = orden de suscripcion. */
  listeners = [];
  /**
   * Registra un listener. Devuelve la funcion para cancelarlo.
   * Si se suscribe el mismo listener dos veces, se invoca dos veces.
   */
  on(listener) {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx >= 0) this.listeners.splice(idx, 1);
    };
  }
  /**
   * Emite un cambio. Espera secuencialmente a cada listener (los async
   * con `await`, los sync se ejecutan en linea). Si un listener lanza,
   * se reporta y se continua con el siguiente (fire-and-forget defensivo).
   */
  async emit(name, value, previous) {
    const snapshot = this.listeners.slice();
    for (const listener of snapshot) {
      try {
        const ret = listener(name, value, previous);
        if (ret && typeof ret.then === "function") {
          await ret;
        }
      } catch (err) {
        try {
          console.error("[xone-macros] listener fallo:", err);
        } catch {
        }
      }
    }
  }
  /** Numero de listeners activos. Util en tests. */
  size() {
    return this.listeners.length;
  }
  /** Vacia el bus (uso interno en logout). */
  clear() {
    this.listeners.length = 0;
  }
};

// src/stores/serialize.ts
function serializeMacroValue(value) {
  if (value === null || value === void 0) return null;
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    return String(value);
  }
  return String(value);
}
function normalizeMacroKey(name) {
  if (typeof name === "number") return String(name);
  return name;
}

// src/stores/GlobalMacroStore.ts
var VALID_MACRO_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;
var WRAPPED_MACRO_NAME = /^##([A-Za-z_][A-Za-z0-9_]*)##$/;
function normalizeMacroName(name) {
  if (typeof name !== "string" || name.length === 0) {
    throw new XoneMacroError(
      "XONE.MACRO.INVALID_NAME",
      `Nombre de macro invalido: "${name}". Debe coincidir con [A-Za-z_][A-Za-z0-9_]* o ##NOMBRE##.`
    );
  }
  const wrapped = WRAPPED_MACRO_NAME.exec(name);
  if (wrapped) return wrapped[1];
  if (VALID_MACRO_NAME.test(name)) return name;
  throw new XoneMacroError(
    "XONE.MACRO.INVALID_NAME",
    `Nombre de macro invalido: "${name}". Debe coincidir con [A-Za-z_][A-Za-z0-9_]* o ##NOMBRE##.`
  );
}
var GlobalMacroStore = class {
  store = /* @__PURE__ */ new Map();
  bus = new MacroChangeBus();
  /**
   * Escribe el valor. Devuelve el valor anterior, o `null` si no existia.
   *
   * Es **sincrono** (paridad con el plan 03 §3.1). Los listeners se disparan
   * en background (microtask) y NO bloquean al llamador. Si el caller
   * necesita coordinarse con listeners async, debe usar
   * `setGlobalMacroAsync(...)` que devuelve `Promise<string | null>` tras
   * que todos los listeners terminen.
   */
  setGlobalMacro(name, value) {
    const normalized = normalizeMacroName(name);
    const serialized = serializeMacroValue(value);
    const previous = this.store.get(normalized) ?? null;
    if (serialized === null) {
      this.store.delete(normalized);
    } else {
      this.store.set(normalized, serialized);
    }
    void this.bus.emit(normalized, serialized, previous);
    return previous;
  }
  /**
   * Variante async que espera a que todos los listeners terminen antes de
   * resolver. Util para flujos donde el caller necesita coordinarse con
   * persistencia o auditoria (uso interno de `09-appdata`).
   */
  async setGlobalMacroAsync(name, value) {
    const normalized = normalizeMacroName(name);
    const serialized = serializeMacroValue(value);
    const previous = this.store.get(normalized) ?? null;
    if (serialized === null) {
      this.store.delete(normalized);
    } else {
      this.store.set(normalized, serialized);
    }
    await this.bus.emit(normalized, serialized, previous);
    return previous;
  }
  /** Devuelve el valor o `null` si no existe. Acepta `##NOMBRE##`. */
  getGlobalMacro(name) {
    const normalized = normalizeMacroName(name);
    return this.store.get(normalized) ?? null;
  }
  /** Devuelve una copia inmutable del mapa completo. */
  getAllGlobalMacros() {
    const out = {};
    for (const [k, v] of this.store) out[k] = v;
    return Object.freeze(out);
  }
  /** Registra un listener; devuelve funcion para desuscribirse. */
  onChange(listener) {
    return this.bus.on(listener);
  }
  /** Limpia el almacen y desconecta listeners (uso interno tras logout). */
  clear() {
    this.store.clear();
  }
  /** Tamano actual (numero de claves). */
  size() {
    return this.store.size;
  }
  /** API interna para tests/diagnostico. */
  _listenerCount() {
    return this.bus.size();
  }
};

// src/stores/CollectionMacroStore.ts
var CollectionMacroStore = class {
  store = /* @__PURE__ */ new Map();
  /** Orden de insercion; misma posicion que la primera escritura. */
  order = [];
  setMacro(name, value) {
    const key = this.resolveKeyForWrite(name);
    if (key === null) return;
    const serialized = serializeMacroValue(value);
    if (serialized === null) {
      if (this.store.delete(key)) {
        const idx = this.order.indexOf(key);
        if (idx >= 0) this.order.splice(idx, 1);
      }
      return;
    }
    if (!this.store.has(key)) this.order.push(key);
    this.store.set(key, serialized);
  }
  getMacro(name) {
    const key = this.resolveKeyForRead(name);
    if (key === null) return null;
    return this.store.get(key) ?? null;
  }
  getAllMacros() {
    const out = {};
    for (const k of this.order) {
      const v = this.store.get(k);
      if (v !== void 0) out[k] = v;
    }
    return Object.freeze(out);
  }
  getMacroCount() {
    return this.store.size;
  }
  clear() {
    this.store.clear();
    this.order.length = 0;
  }
  /**
   * Resuelve la clave para escritura. Si llega un indice numerico, exige
   * que apunte a una clave ya existente (no podemos inventar el nombre).
   */
  resolveKeyForWrite(name) {
    if (typeof name === "number") {
      if (!Number.isInteger(name) || name < 0 || name >= this.order.length) {
        return null;
      }
      return this.order[name] ?? null;
    }
    return normalizeMacroKey(name);
  }
  /**
   * Resuelve la clave para lectura: el indice numerico apunta al N-esimo
   * elemento insertado. Si el indice es invalido devolvemos `null`.
   */
  resolveKeyForRead(name) {
    if (typeof name === "number") {
      if (!Number.isInteger(name) || name < 0 || name >= this.order.length) {
        return null;
      }
      return this.order[name] ?? null;
    }
    return normalizeMacroKey(name);
  }
};

// src/stores/ObjectMacroAdapter.ts
var ObjectMacroAdapter = class {
  fields = /* @__PURE__ */ new Map();
  constructor(initial) {
    if (initial) {
      for (const [k, v] of Object.entries(initial)) {
        this.fields.set(this.canonical(k), v);
      }
    }
  }
  setField(name, value) {
    const key = this.canonical(normalizeMacroKey(name));
    const serialized = serializeMacroValue(value);
    if (serialized === null) {
      this.fields.delete(key);
      return;
    }
    this.fields.set(key, serialized);
  }
  getField(name) {
    return this.fields.get(this.canonical(name)) ?? null;
  }
  /**
   * Devuelve el mapa completo en formato `FLD_CAMPO -> valor`. Listo para
   * pasar a `replaceMacros({ objectFields })`.
   */
  asObjectFields() {
    const out = {};
    for (const [k, v] of this.fields) {
      out[k] = v;
    }
    return out;
  }
  clear() {
    this.fields.clear();
  }
  size() {
    return this.fields.size;
  }
  /**
   * Normaliza la clave: garantiza que viva con prefijo `FLD_`. Asi el
   * caller puede usar tanto `setField('CODIGO')` como `setField('FLD_CODIGO')`.
   */
  canonical(name) {
    if (!name) return name;
    return name.startsWith("FLD_") ? name : `FLD_${name}`;
  }
};

// src/replace/tokenizer.ts
var MACRO_TOKEN_REGEX = /##([A-Za-z_][A-Za-z0-9_]*)##/g;
function containsMacroToken(template) {
  MACRO_TOKEN_REGEX.lastIndex = 0;
  return MACRO_TOKEN_REGEX.test(template);
}

// src/replace/replaceMacros.ts
var MACRO_MAX_DEPTH = 8;
function resolveOnce(name, scope) {
  if (name.startsWith("FLD_") && scope.objectFields) {
    const v = scope.objectFields[name];
    if (v !== void 0) return v;
    const bare = scope.objectFields[name.slice(4)];
    if (bare !== void 0) return bare;
  }
  if (scope.collectionMacros && scope.collectionMacros[name] !== void 0) {
    return scope.collectionMacros[name];
  }
  if (scope.globalMacros && scope.globalMacros[name] !== void 0) {
    return scope.globalMacros[name];
  }
  if (scope.systemMacros && scope.systemMacros[name] !== void 0) {
    return scope.systemMacros[name];
  }
  return void 0;
}
function replaceMacros(template, scope) {
  if (typeof template !== "string" || template.length === 0) return template;
  if (template.indexOf("##") < 0) return template;
  let current = template;
  let depth = 0;
  while (depth < MACRO_MAX_DEPTH) {
    let changed = false;
    MACRO_TOKEN_REGEX.lastIndex = 0;
    current = current.replace(MACRO_TOKEN_REGEX, (match, name) => {
      const value = resolveOnce(name, scope);
      if (value === void 0) {
        return match;
      }
      changed = true;
      return value;
    });
    if (!changed) return current;
    depth++;
  }
  if (current.indexOf("##") >= 0) {
    MACRO_TOKEN_REGEX.lastIndex = 0;
    if (MACRO_TOKEN_REGEX.test(current)) {
      throw new XoneMacroError(
        "XONE.MACRO.RECURSIVE",
        `Resolucion de macros excedio MAX_DEPTH=${MACRO_MAX_DEPTH}. Posible ciclo en el template: "${template}".`
      );
    }
  }
  return current;
}

// src/replace/replaceMacrosSql.ts
function replaceMacrosSql(sql, scope, options = {}) {
  if (typeof sql !== "string" || sql.length === 0) return sql;
  const scopeWithPrefix = options.prefix !== void 0 ? {
    ...scope,
    systemMacros: {
      ...scope.systemMacros ?? {},
      PREF: options.prefix
    }
  } : scope;
  const replaced = replaceMacros(sql, scopeWithPrefix);
  if (!options.escapeSql) return replaced;
  return escapeSingleQuotesInsideLiterals(replaced);
}
function escapeSingleQuotesInsideLiterals(sql) {
  let out = "";
  let i = 0;
  while (i < sql.length) {
    const ch = sql[i];
    if (ch !== "'") {
      out += ch;
      i++;
      continue;
    }
    out += "'";
    i++;
    while (i < sql.length) {
      const c = sql[i];
      if (c === "'") {
        if (sql[i + 1] === "'") {
          out += "''";
          i += 2;
          continue;
        }
        const next = sql[i + 1];
        if (next === void 0 || isSqlBoundary(next)) {
          out += "'";
          i++;
          break;
        }
        out += "''";
        i++;
        continue;
      }
      out += c;
      i++;
    }
  }
  return out;
}
function isSqlBoundary(c) {
  return c === " " || c === "	" || c === "\n" || c === "\r" || c === "," || c === ")" || c === "(" || c === ";" || c === "=" || c === "<" || c === ">" || c === "+" || c === "-" || c === "*" || c === "/" || c === "|" || c === "%";
}

// src/system/createSystemMacros.ts
function createSystemMacros(opts) {
  const {
    prefix,
    appVersion,
    deviceOs = "Web",
    getUserId,
    globalStore,
    nowProvider = () => /* @__PURE__ */ new Date()
  } = opts;
  const resolveUserId = () => {
    if (getUserId) return getUserId() ?? "";
    if (globalStore) return globalStore.getGlobalMacro("ID_USUARIO") ?? "";
    return "";
  };
  const resolveVersion = () => {
    const fromMacro = globalStore?.getGlobalMacro("APP_VERSION");
    return fromMacro ?? appVersion;
  };
  const formatNow = () => {
    const d = nowProvider();
    const iso = d.toISOString();
    return iso.replace(/\.\d{3}Z$/, "Z");
  };
  const formatNowTime = () => {
    const d = nowProvider();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  };
  return {
    get PREF() {
      return prefix;
    },
    get DEVICE_OS() {
      return deviceOs;
    },
    get VERSION() {
      return resolveVersion();
    },
    get USERID() {
      return resolveUserId();
    },
    get NOW() {
      return formatNow();
    },
    get NOW_TIME() {
      return formatNowTime();
    },
    snapshot() {
      return {
        PREF: prefix,
        DEVICE_OS: deviceOs,
        VERSION: resolveVersion(),
        USERID: resolveUserId(),
        NOW: formatNow(),
        NOW_TIME: formatNowTime()
      };
    }
  };
}

// src/system/bootstrap.ts
var SYSTEM_BOOTSTRAP_MACROS = Object.freeze([
  "API_BASE",
  "TOKEN",
  "REFRESH_TOKEN",
  "ID_USUARIO",
  "ID_EMPRESA",
  "DEVICE_ID",
  "REPLICATION_ID",
  "LAST_CHECKPOINT"
]);
function bootstrapSystemMacros(store, options = {}) {
  const { initialValues = {}, overwrite = false } = options;
  for (const name of SYSTEM_BOOTSTRAP_MACROS) {
    const current = store.getGlobalMacro(name);
    if (current !== null && !overwrite) continue;
    const value = initialValues[name] ?? "";
    store.setGlobalMacro(name, value);
  }
}

// src/system/createBrowserMacros.ts
function createBrowserMacros(opts) {
  const env = opts.env ?? getDefaultEnv();
  if (!env) return {};
  const { screen, devicePixelRatio, innerWidth, innerHeight, navigator } = env;
  const ua = navigator.userAgent;
  const lang = navigator.language ?? "";
  const [langCode = "", countryCode = ""] = lang.split("-");
  const dpr = devicePixelRatio > 0 ? devicePixelRatio : 1;
  const densityBucket = bucketDensity(dpr);
  const densityValue = String(Math.round(dpr * 160));
  const orientation = innerWidth >= innerHeight ? "landscape" : "portrait";
  const deviceType = classifyDeviceType(innerWidth, innerHeight, ua);
  const { manufacturer, model, osVersion } = parseBrowserUa(ua);
  const out = {
    SCREEN_RESOLUTION_WIDTH: String(screen.width),
    SCREEN_RESOLUTION_HEIGHT: String(screen.height),
    CURRENT_LANGUAGE: langCode,
    CURRENT_COUNTRY_CODE: countryCode.toUpperCase(),
    CURRENT_DENSITY: densityBucket,
    CURRENT_DENSITY_VALUE: densityValue,
    CURRENT_ORIENTATION: orientation,
    DEVICE_TYPE: deviceType,
    DEVICE_MANUFACTURER: manufacturer,
    DEVICE_MODEL: model,
    DEVICE_OSVERSION: osVersion,
    // FRAME_VERSION y LIVEUPDATE_VERSION ambos apuntan a la version del SPA
    // (web no diferencia entre framework y live-update — el SPA se
    // republica atomicamente como un solo bundle).
    FRAME_VERSION: opts.appVersion,
    LIVEUPDATE_VERSION: opts.appVersion
  };
  if (opts.deviceId !== void 0 && opts.deviceId.length > 0) {
    out.DEVICEID = opts.deviceId;
  }
  return out;
}
function bucketDensity(dpr) {
  if (dpr <= 0.75) return "ldpi";
  if (dpr <= 1) return "mdpi";
  if (dpr <= 1.5) return "hdpi";
  if (dpr <= 2) return "xhdpi";
  if (dpr <= 3) return "xxhdpi";
  return "xxxhdpi";
}
function classifyDeviceType(w, h, ua) {
  const uaLower = ua.toLowerCase();
  if (uaLower.includes("wearos") || uaLower.includes("android wear")) {
    return "watch_square_device";
  }
  const min = Math.min(w, h);
  const max = Math.max(w, h);
  if (max >= 1024) return "tablet_device";
  if (max <= 320) return "mini_device";
  if (min >= 400 && max >= 800) return "highphone_device";
  return "phone_device";
}
function parseBrowserUa(ua) {
  if (!ua) return { manufacturer: "Unknown", model: "Unknown", osVersion: "" };
  const tests = [
    { re: /Edg\/(\d+(?:\.\d+)*)/, manufacturer: "Microsoft", model: "Edge" },
    { re: /OPR\/(\d+(?:\.\d+)*)/, manufacturer: "Opera", model: "Opera" },
    { re: /Firefox\/(\d+(?:\.\d+)*)/, manufacturer: "Mozilla", model: "Firefox" },
    { re: /Chrome\/(\d+(?:\.\d+)*)/, manufacturer: "Google", model: "Chrome" },
    { re: /Version\/(\d+(?:\.\d+)*).*Safari/, manufacturer: "Apple", model: "Safari" }
  ];
  for (const t of tests) {
    const m = t.re.exec(ua);
    if (m) {
      return { manufacturer: t.manufacturer, model: t.model, osVersion: m[1] ?? "" };
    }
  }
  return { manufacturer: "Unknown", model: "Unknown", osVersion: "" };
}
function getDefaultEnv() {
  if (typeof globalThis === "undefined") return null;
  const g = globalThis;
  if (!g.screen || !g.navigator) return null;
  return {
    screen: g.screen,
    devicePixelRatio: g.devicePixelRatio ?? 1,
    innerWidth: g.innerWidth ?? g.screen.width,
    innerHeight: g.innerHeight ?? g.screen.height,
    navigator: g.navigator
  };
}

// src/index.ts
var _defaultGlobalStore = new GlobalMacroStore();
function setGlobalMacro(name, value) {
  return _defaultGlobalStore.setGlobalMacro(name, value);
}
function getGlobalMacro(name) {
  return _defaultGlobalStore.getGlobalMacro(name);
}
function getAllGlobalMacros() {
  return _defaultGlobalStore.getAllGlobalMacros();
}
function onMacroChange(listener) {
  return _defaultGlobalStore.onChange(listener);
}
function _resetDefaultGlobalStore() {
  _defaultGlobalStore.clear();
}
function _getDefaultGlobalStore() {
  return _defaultGlobalStore;
}
export {
  CollectionMacroStore,
  GlobalMacroStore,
  MACRO_MAX_DEPTH,
  MACRO_TOKEN_REGEX,
  MacroChangeBus,
  ObjectMacroAdapter,
  SYSTEM_BOOTSTRAP_MACROS,
  XoneMacroError,
  _getDefaultGlobalStore,
  _resetDefaultGlobalStore,
  bootstrapSystemMacros,
  containsMacroToken,
  createBrowserMacros,
  createSystemMacros,
  getAllGlobalMacros,
  getGlobalMacro,
  onMacroChange,
  replaceMacros,
  replaceMacrosSql,
  setGlobalMacro
};
//# sourceMappingURL=index.js.map